import { assert } from '@fe/core';
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../hooks/store-hooks';
import { useSafeEffect } from '../../hooks/use-safe-effect';
import { ROUTES } from '../../router/routes';
import { closeWindow, updateWindowBounds, WorkspaceWindowState } from '../../state/slices/workspace-slice';
import { WindowBounds } from './types';

const externalWindows = new Map<number, WindowProxy>();

window.addEventListener('beforeunload', () => {
    externalWindows.values().forEach((x) => x.close());
});

export function focusExternalWindow(windowId: number): boolean {
    const win = externalWindows.get(windowId);
    if (!win || win.closed) {
        return false;
    }

    win.focus();
    return true;
}

export function ExternalWindowPortal(props: {
    window: WorkspaceWindowState;
    children?: React.ReactNode;
    onCreate?: (window: WindowProxy, root: HTMLElement) => void;
}) {
    const [root, setRoot] = React.useState<HTMLElement>();
    const windowBounds = useRef<WindowBounds>(props.window.bounds);
    const [externalWindow, setExternalWindow] = React.useState<WindowProxy>();
    const isDark = useAppSelector((x) => x.settings.isDark);
    const dispatch = useDispatch();

    useSafeEffect(() => {
        let sizeString = '';
        if (props.window.bounds) {
            sizeString = `,screenX=${props.window.bounds.screenX},screenY=${props.window.bounds.screenY},width=${props.window.bounds.width},height=${props.window.bounds.height}`;
        }

        const newWindow = window.open(`${ROUTES.MULTI_WINDOW}`, '_blank', 'popup=true' + sizeString)!;
        // This will be null if user denied permission
        if (!newWindow) {
            return;
        }

        newWindow.onmessage = (e) => {
            if (e.data.type === 'ready') {
                const root = newWindow.document.getElementById('window-content') ?? undefined;
                assert(root);
                setRoot(root);
                newWindow.document.title = 'Flight Explorer - ' + props.window.name;
                props.onCreate?.(newWindow, root);
            } else if (e.data.type === 'closing') {
                saveWindowBounds();
                dispatch(closeWindow({ id: props.window.id }));
                setRoot(undefined);
            }
        };

        const saveWindowBounds = () => {
            if (
                windowBounds.current &&
                windowBounds.current.screenX === newWindow.screenX &&
                windowBounds.current.screenY === newWindow.screenY &&
                windowBounds.current.width === newWindow.innerWidth &&
                windowBounds.current.height === newWindow.innerHeight
            ) {
                return;
            }

            windowBounds.current = {
                screenX: newWindow.screenX,
                screenY: newWindow.screenY,
                width: newWindow.innerWidth,
                height: newWindow.innerHeight
            };
            dispatch(updateWindowBounds({ id: props.window.id, bounds: windowBounds.current }));
        };

        // There's no window reposition event, so check for bounds changed on focus and blur
        newWindow.addEventListener('focus', saveWindowBounds);
        newWindow.addEventListener('blur', saveWindowBounds);

        const contentLoaded = () => {
            externalWindows.set(props.window.id, newWindow);
            setExternalWindow(newWindow);
        };
        newWindow.addEventListener('DOMContentLoaded', contentLoaded, { once: true });
        return () => {
            newWindow.close();
            setExternalWindow(undefined);
            newWindow.removeEventListener('focus', saveWindowBounds);
            newWindow.removeEventListener('blur', saveWindowBounds);
            newWindow.removeEventListener('DOMContentLoaded', contentLoaded);
            externalWindows.delete(props.window.id);
        };
    }, []);

    // Sync theme
    useEffect(() => {
        if (externalWindow) {
            externalWindow.document.documentElement.dataset['isDark'] = isDark.toString();
        }
    }, [externalWindow, isDark]);

    if (!root) {
        return;
    }

    return createPortal(props.children, root);
}
